import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "@components";
import {Category, Gender, CategoryItem, Lookups} from "@model";
import {CategoriesService, TOAST_STATE, ToastService} from "@services";
import {required} from "@utility";

const MIN_AGE = 0;
const MAX_AGE = 110;

@Component({
  selector: 'app-category-form',
  templateUrl: './category-form.component.html',
  styleUrls: ['./category-form.component.scss']
})
export class CategoryFormComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input() category: Category | undefined;
  @Input() organiserId: number | undefined;
  @Input() categoryTypes: Lookups[] | undefined;
  @Output() cancel = new EventEmitter<any>();

  maleCategoryItemsValid: boolean = true;
  femaleCategoryItemsValid: boolean = true;
  inputForm = new FormGroup({
    name: new FormControl(),
    categoryType: new FormControl(),
    itemsMale: this.formBuilder.array([]),
    itemsFemale: this.formBuilder.array([]),
  });


  constructor(public translocoService: TranslocoService, public formBuilder: FormBuilder,
              public categoriesService: CategoriesService, private toastService: ToastService) {
    super();
  }

  ngOnInit() {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['category']?.currentValue !== changes['category']?.previousValue) {
      this.initForm();
    }
  }

  addCategoryItem(array: FormArray, categoryItem: CategoryItem) {
    array.push(this.formBuilder.group({
      name: [categoryItem.name, required],
      code: [categoryItem.code, required],
      ageFrom: [categoryItem.ageFrom, required],
      ageTo: [categoryItem.ageTo, required],
      gender: [categoryItem.gender, required],
      active: [categoryItem.active, required]
    }));
  }

  initForm() {
    this.inputForm.markAsUntouched();
    this.inputForm.controls.name.addValidators([required]);
    this.inputForm.controls.categoryType.addValidators([required]);
    this.inputForm.controls.name.setValue(this.category?.name);
    this.inputForm.controls.categoryType.setValue(this.category?.categoryType);
    this.inputForm.controls.itemsMale = this.formBuilder.array([]);
    this.inputForm.controls.itemsFemale = this.formBuilder.array([])
    this.categoryItemsFemale.forEach((categoryItem: CategoryItem) => {
      this.addCategoryItem(this.itemsFemaleFormArray, categoryItem)
    });
    this.categoryItemsMale.forEach((categoryItem: CategoryItem) => {
      this.addCategoryItem(this.itemsMaleFormArray, categoryItem)
    });

    this.maleCategoryItemsValid = true;
    this.femaleCategoryItemsValid = true;
  }

  get itemsMaleFormArray(): FormArray {
    return this.inputForm.controls.itemsMale as FormArray;
  }

  get itemsFemaleFormArray(): FormArray {
    return this.inputForm.controls.itemsFemale as FormArray;
  }

  get categoryItemsMale(): CategoryItem[] {
    return this.category?.items?.filter(item => item.gender === Gender.MALE) ?? [];
  }

  get categoryItemsFemale(): CategoryItem[] {
    return this.category?.items?.filter(item => item.gender === Gender.FEMALE) ?? [];
  }

  addMaleCategoryItems() {
    let categoryItem = new CategoryItem();
    categoryItem.active = false;
    categoryItem.gender = Gender.MALE;
    this.inputForm.controls.itemsMale = this.formBuilder.array([])
    this.addCategoryItem(this.inputForm.controls.itemsMale, categoryItem)

  }

  addFemaleCategoryItems() {
    let categoryItem = new CategoryItem();
    categoryItem.active = false;
    categoryItem.gender = Gender.FEMALE;
    this.inputForm.controls.itemsFemale = this.formBuilder.array([])
    this.addCategoryItem(this.inputForm.controls.itemsFemale, categoryItem)

  }

  private isValid(): boolean {
    return this.inputForm.controls.name.valid && this.inputForm.controls.categoryType.valid
      && this.inputForm.controls.itemsMale.valid
      && this.inputForm.controls.itemsFemale.valid
      && (this.itemsFemaleFormArray.controls.length > 0 || this.itemsMaleFormArray.controls.length > 0)
  }

  getCategoryFields(): Category {
    let category: Category = {...this.category};
    category.organiserId = this.organiserId;
    let items: CategoryItem[] = [];
    this.inputForm.controls.itemsFemale.getRawValue().forEach((item) => {
      let categoryItem: CategoryItem = item as CategoryItem;
      categoryItem.categoryId = category.id;
      items.push(categoryItem);
    });

    this.inputForm.controls.itemsMale.getRawValue().forEach(item => {
      let categoryItem: CategoryItem = item as CategoryItem;
      categoryItem.categoryId = category.id;
      items.push(categoryItem);
    });
    category.name = this.inputForm.value.name;
    category.categoryType = this.inputForm.value.categoryType;
    category.items = CategoryItem.groupAndSortCategoryItems(items);
    return category;

  }


  isCategoryItemsValid(categoryItems: CategoryItem[]): boolean {
    if (categoryItems.length > 0) {
      let hasError: boolean = false;
      let valueSet = new Set();
      let allInactive: boolean = true;
      categoryItems.forEach((item: CategoryItem) => {
        if (item.ageFrom !== undefined && item.ageTo !== undefined) {
          if (item.ageFrom >= item.ageTo || item.ageFrom < MIN_AGE || item.ageTo > MAX_AGE) {
            hasError = true;
            return;
          }
          for (let i = item.ageFrom; i <= item.ageTo; i++) {
            if (valueSet.has(i)) {
              hasError = true;
              return;
            } else {
              valueSet.add(i);
            }
          }
          if (item.active) {
            allInactive = false;
          }
        } else {
          hasError = true;
          return;
        }
      });
      if (valueSet.size !== (MAX_AGE - MIN_AGE) + 1) {
        hasError = true;
      }
      return !(hasError || allInactive);
    } else return true;
  }

  onSave(): void {
    if (!this.isValid()) {
      this.markAllAsTouched(this.inputForm);
      return;
    } else {
      let category: Category = this.getCategoryFields();
      this.maleCategoryItemsValid = this.isCategoryItemsValid((category.items ?? []).filter(item => item.gender === Gender.MALE));
      this.femaleCategoryItemsValid = this.isCategoryItemsValid((category.items ?? []).filter(item => item.gender === Gender.FEMALE));
      if (this.femaleCategoryItemsValid && this.maleCategoryItemsValid) {
        this.categoriesService.updateCategory(this.organiserId, category, () => {
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("app.common.success.category.edit"))
          this.onCancel();
        }, () => {
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.common.server.error"))
          this.onCancel();
        });
      }
    }
  }


  onCancel() {
    this.category = undefined;
    this.initForm();
    this.cancel.next(true);
  }

  deleteCategoryItem(gender: number) {
    switch (gender) {
      case Gender.MALE:
        this.inputForm.controls.itemsMale = this.formBuilder.array([])
        break;
      case Gender.FEMALE:
        this.inputForm.controls.itemsFemale = this.formBuilder.array([])
        break;
      default:
        break;
    }
  }
}

